import React, { useState, useEffect, useCallback } from 'react';
import './Contact.css';
import { apiFetch } from '../../utils/apiClient';

const ContactForm = () => {

    // local development uses env.development, production routes to /api
    const API_URL = process.env.REACT_APP_API_URL || "/api";

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');

    const [triggerValidation, setTriggerValidation] = useState(false);
    const [isButtonClicked, setIsButtonClicked] = useState(false);


    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });

        if (isButtonClicked) {
            // continuously validate form on input change
            setTriggerValidation(true);
        }
    };


    const validateForm = useCallback(() => {

        if (!isButtonClicked) {
            // capture first time button has been clicked
            setIsButtonClicked(true);
        }

        const errors = {};
        if (!formData.name) {
            errors.name = true;
        }
        if (!formData.email) {
            errors.email = true;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = true;
        }
        if (!formData.message) {
            errors.message = true;
        }
        setValidationErrors(errors);

        // determine if form is valid
        const isValid = Object.keys(errors).length === 0

        // Reset trigger after validation so it will be called again if set true
        if (triggerValidation)
            setTriggerValidation(false);

        return isValid;
    }, [isButtonClicked, formData, triggerValidation]);


    useEffect(() => {
        if (triggerValidation) {
            validateForm();
        }
    }, [triggerValidation, validateForm]);


    const handleSubmit = async (e) => {

        if (!validateForm()) {
            return;
        }

        const payload = {
            name: formData.name.trim(),
            from_email: formData.email.trim().toLowerCase(),
            message: formData.message.trim(),
        };

        try {

            const contactUsURL = `${API_URL}/contact`;

            const response = await apiFetch(contactUsURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`${data.error}`);
            }

            setIsModalOpen(true);

        } catch (error) {
            // catch any errors, i.e. ones not caught by the fetch call
            // and also the one thrown by the fetch call
            setApiErrorMessage(error.message);
            setIsErrorModalOpen(true);
        }

    };


    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({ name: '', email: '', message: '', isFormValid: false });
    };

    return (
        <div className="form-container">
            <div>
                <div className={'contact-form-input' + (validationErrors.name ? ' contact-error-icon' : '')}>
                    <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className={'contact-form-input ' + (validationErrors.email ? ' contact-error-icon' : '')}>
                    <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className={'contact-form-input' + (validationErrors.message ? ' contact-error-icon' : '')}>
                    <textarea
                        name="message"
                        placeholder="Your message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                    ></textarea>
                </div>
                <div className="contact-button-container">
                    <button
                        type="submit"
                        onClick={handleSubmit}
                    >
                        Send
                    </button>
                </div>
            </div>
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <p>Thank you for your message. We typically aim to respond within 2 working days.</p>
                        <button onClick={closeModal}>
                            Close
                        </button>
                    </div>
                </div>
            )}
            {isErrorModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <p>
                            Sorry, something went wrong and your message couldn't be delivered. Please try again or get in touch at <a href='help@thetypingrooms.co.uk'> help@thetypingrooms.co.uk</a>
                        </p>
                        <p className='error-code'>
                            {apiErrorMessage}
                        </p>
                        <button onClick={() => {
                            setIsErrorModalOpen(false);
                            setFormData({ name: '', email: '', message: '' });
                        }}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactForm;
